// interaactive listening import React, { useState, useRef, useEffect } from "react";
import { keyframes, ToggleButton } from "@mui/material";
import MicIcon from "@mui/icons-material/MicRounded";
import { useAppDataContext } from "../../../../framework/app/app.data.context";
import { AiMessage } from "../../../../api-clients/composition";
import { useEffect, useRef, useState } from "react";
import { getConfig } from "../../../../framework/config/app.config";

const csApiUrl = getConfig('VITE_BASE_URL');

// Create keyframes for the pulsing animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

// Define the props interface if not already defined
export interface VoiceIOPrompts {
  loading: boolean;
  onSendMessage: (message: string) => void;
  onStop: () => void;
  responseMessages: AiMessage[];
}

function VoiceIO({
  loading,
  onSendMessage,
  onStop,
  responseMessages,
}: VoiceIOPrompts) {
  const [isRecording, setIsRecording] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const { appContext } = useAppDataContext();

  // Refs for silence detection
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const silenceStartRef = useRef<number | null>(null);

  const SILENCE_THRESHOLD = 0.01; // RMS threshold for silence
  const SILENCE_DURATION = 2000; // Duration (ms) to consider as silence

  useEffect(() => {
    console.log('checking audio', responseMessages?.length, isDictating);
    if (responseMessages && responseMessages.length > 0 && isDictating) {
      // Concatenate the content of each message to form the complete text
      const text = responseMessages.reduce(
        (acc, cur) => acc + cur.content[0].text,
        ""
      );
      textToSpeechAudio(text);
      setIsDictating(loading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseMessages]);

  const startRecording = async () => {
    const stream = await getMicrophoneAccess();
    if (!stream) return;

    let mimeType = "audio/webm";
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      // mimeType = "audio/ogg; codecs=opus";
      mimeType = "audio/mp4"; // iOS often uses this internally

      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/ogg"; // Fallback option
      }
    }

    const options = mimeType ? { mimeType } : undefined;
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorderRef.current = mediaRecorder;

    // Reset the chunks array
    audioChunksRef.current = [];

    // Initialize AudioContext for silence detection
    try {
      const AudioContextConstructor =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextConstructor) {
        console.error("Web Audio API is not supported in this browser.");
        alert("Web Audio API is not supported in this browser.");
        return;
      }
      audioContextRef.current = new AudioContextConstructor();
    } catch (error) {
      console.error("Error initializing AudioContext:", error);
      alert("Error initializing audio processing.");
      return;
    }

    if (!audioContextRef.current) {
      console.error("AudioContext could not be created.");
      return;
    }

    const source = audioContextRef.current.createMediaStreamSource(stream);
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 2048;
    source.connect(analyserRef.current);

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const detectSilence = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const sample = dataArray[i] / 128 - 1.0; // Normalize to [-1, 1]
        sum += sample * sample;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      if (rms < SILENCE_THRESHOLD) {
        if (!silenceStartRef.current) {
          silenceStartRef.current = Date.now();
        } else {
          const elapsed = Date.now() - silenceStartRef.current;
          if (elapsed > SILENCE_DURATION) {
            // Silence detected for enough time
            stopRecording();
          }
        }
      } else {
        silenceStartRef.current = null; // Reset silence timer
      }
    };

    // Set up interval to check for silence every 100ms
    silenceTimerRef.current = setInterval(detectSilence, 100);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, {
        type: mimeType || "audio/webm",
      });
      speechAudioToText(audioBlob);
    };

    mediaRecorder.start(1000); // Use a timeslice here, if you do not, IOS audio streams fail to transcribe
    setIsRecording(true);
    setIsDictating(true);
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    // Stop the microphone stream
    const stream = mediaRecorderRef.current?.stream;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    // Clear silence detection timer
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // Close AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Reset silence start
    silenceStartRef.current = null;
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const getMicrophoneAccess = async (): Promise<MediaStream | null> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return stream;
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Microphone access is required to use voice input.");
      return null;
    }
  };

  const speechAudioToText = async (audioBlob: Blob) => {

    const formData = new FormData();
    formData.append(
      "file",
      audioBlob,
      `recording.${audioBlob.type.split("/")[1] || "webm"}`
    );

    const { httpClient } = appContext;
    const url = `${csApiUrl}/ai/speechToText`;

    try {
      const { body } = await httpClient(url, {
        method: "POST",
        body: formData,
      });

      // Assuming the backend returns JSON as a string
      const result = typeof body === "string" ? JSON.parse(body) : body;

      // Handle the transcription result
      if (result && result.transcription && result.transcription.length > 0) {
        onSendMessage(result.transcription);
      }
    } catch (error) {
      console.error("Error sending audio to backend:", error);
      alert("There was an error processing your audio. Please try again.");
    }
  };

  const textToSpeechAudio = async (text: string) => {
    console.log('text to speech');
    const { tokenProvider } = appContext;
    const url = `${csApiUrl}/ai/textToSpeech`;

    if (isAudioPlaying) return;

    try {
      const token = await tokenProvider.getIdToken();
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ input: text }),
      });

      if (!response.ok) {
        console.error("Error in response", response.statusText);
        alert("There was an error generating the audio. Please try again.");
        return;
      }

      // Get the response as a Blob
      const blob = await response.blob();

      // Create a URL for the Blob
      const audioUrl = URL.createObjectURL(blob);

      // Create an audio element and play the audio
      const audio = new Audio(audioUrl);
      // Ensure audio play is initiated through user interaction
      const playAudio = async () => {
        try {
          await audio.play();
          console.log("Audio playback started.");
        } catch (error) {
          console.warn("Playback failed. Showing tap-to-play overlay:", error);
          showPlayOverlay(playAudio); 
        }
      };

      if (audio.paused) {
        await playAudio(); // Try playing audio directly
      }
      // audio.play();

      // Optional: Clean up the object URL after the audio has finished playing
      audio.onended = () => {
        setIsAudioPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };
    } catch (error) {
      console.error("Error sending text to backend for TTS:", error);
      alert("There was an error generating the audio. Please try again.");
    }
  };

  // Helper to show a tap-to-play overlay
  const showPlayOverlay = (playAudio: () => void) => {
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    overlay.style.background = "rgba(0, 0, 0, 0.7)";
    overlay.style.color = "white";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.fontSize = "24px";
    overlay.style.zIndex = "1000";
    overlay.innerText = "Tap to play the audio";

    // Add the event listener for user interaction
    overlay.addEventListener("click", () => {
      playAudio(); // Trigger audio playback
      document.body.removeChild(overlay); // Remove the overlay after interaction
    });

    document.body.appendChild(overlay); // Add overlay to the page
  };

  return (
    <div className="chat-reset-section">
      <ToggleButton
        selected={isRecording}
        value="recording"
        color={isRecording ? "secondary" : "primary"}
        aria-label={isRecording ? "Stop recording" : "Start recording"}
        className={`mic-icon ${isRecording ? "mic-button-listening" : ""}`}
        onChange={handleToggleRecording}
        sx={{
          position: "relative",
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: '12px',
          color: 'rgba(255, 255, 255, 0.85)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          transition: 'background 0.3s ease, transform 0.2s ease',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.2)',
            transform: 'scale(1.08)',
          },
          '& svg': {
            zIndex: 1,
            position: 'relative',
          },
          "&::before": isRecording
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                margin: "auto",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                backgroundColor: "rgba(0, 123, 255, 0.5)",
                animation: `${pulse} 1.5s infinite ease-out`,
                zIndex: 0,
              }
            : {},
        }}
      >
        <MicIcon />
      </ToggleButton>
    </div>
  );
}

export default VoiceIO;

// working before interactive listening
/*
import React, { useState, useRef, useEffect } from "react";
import { IconButton, ToggleButton } from "@mui/material";
import MicIcon from "@mui/icons-material/MicRounded";
import { PromptProps } from "../PromptSection";
import { useAppDataContext } from "../../../framework/app/app.data.context";
import { AiMessage } from "../../../api-clients/composition";

export interface VoiceIOPrompts {
  loading: boolean;
  onSendMessage: (message: string) => void;
  onStop: () => void;
  responseMessages: AiMessage[];
}

function VoiceIO({ loading, onSendMessage, onStop, responseMessages }: VoiceIOPrompts) {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const { appContext } = useAppDataContext();

  useEffect(() => {
   const text = '';

   if (responseMessages && responseMessages?.length > 0) { 
    // concat the content of each message to text
    const text = responseMessages.reduce((acc, cur) => acc + cur.content[0].text, '');

    textToSpeechAudio(text);
   }


  }, [responseMessages]);

  const startRecording = async () => {
    const stream = await getMicrophoneAccess();
    if (!stream) return;

    let mimeType = "audio/webm";
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = "audio/ogg; codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "";
      }
    }

    const options = mimeType ? { mimeType } : undefined;
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorderRef.current = mediaRecorder;

    // Reset the chunks array
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || "audio/webm" });
      speechAudioToText(audioBlob);
    };

    mediaRecorder.start(); // Start recording without timeslice
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    // Stop the microphone stream
    const stream = mediaRecorderRef.current?.stream;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const getMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return stream;
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Microphone access is required to use voice input.");
      return null;
    }
  };

  const speechAudioToText = async (audioBlob: Blob) => {
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.webm");

    const { httpClient } = appContext;
    const url = `${csApiUrl}/ai/speechToText`;

    try {
      const { body } = await httpClient(url, {
        method: "POST",
        body: formData,
      });

      const result = JSON.parse(body);
      // Optionally, handle the transcription result
      onSendMessage(result.transcription);
    } catch (error) {
      console.error("Error sending audio to backend:", error);
    }
  };

  const textToSpeechAudio = async (text: string) => {
    const { tokenProvider } = appContext;
    const url = `${csApiUrl}/ai/textToSpeech`;

    try {
      const token = await tokenProvider.getIdToken();
      const response = await fetch(url, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ input: text }),
      });
  
      if (!response.ok) {
        console.error('Error in response', response.statusText);
        return;
      }
  
      // Get the response as a Blob
      const blob = await response.blob();
  
      // Create a URL for the Blob
      const audioUrl = URL.createObjectURL(blob);
  
      // Create an audio element and play the audio
      const audio = new Audio(audioUrl);
      audio.play();
  
      // Optional: Clean up the object URL after the audio has finished playing
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
    } catch (error) {
      console.error("Error sending audio to backend:", error);
    }
  };

  return (
    <div className="chat-reset-section">
      <ToggleButton
        selected={isRecording}
        value="recording"
        color={isRecording ? "secondary" : "primary"}
        aria-label={isRecording ? "Stop recording" : "Start recording"}
        onChange={handleToggleRecording}
      >
        <MicIcon />
      </ToggleButton>
    </div>
  );
}

export default VoiceIO;
*/
